<template>
    <div v-if="editor" class="w-full bg-gray-100 border border-gray-200 rounded-md">
        <div>
            <div class="flex flex-wrap items-center py-2">
              <div class="pr-2">
                <slot></slot>
              </div>
              <div class="flex flex-wrap border-r border-gray-300 pr-2">
                <!-- Preset swatches are buttons, not a <datalist> on the color input:
                     a datalist selection only fires `input` when the value actually
                     changes, so picking the color already in the input (e.g. the
                     default black) applied nothing until you detoured through
                     another color first. -->
                <div class="relative" v-click-outside="closeColorPicker">
                  <button
                    class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap flex flex-col items-center justify-center leading-none"
                    title="Text color"
                    :style="showColorPicker ? 'background-color: #E5E5E5;' : ''"
                    @click.prevent="showColorPicker = !showColorPicker"
                  >
                    <span class="text-xs">A</span>
                    <span class="block w-4 h-1 rounded-sm border border-gray-400" :style="{ backgroundColor: currentColor }"></span>
                  </button>
                  <div
                    v-if="showColorPicker"
                    class="absolute left-0 top-7 z-20 w-40 p-2 bg-white border border-gray-300 rounded-md shadow-lg"
                  >
                    <div class="grid grid-cols-5 gap-1">
                      <button
                        v-for="preset in colorPresets"
                        :key="preset.value"
                        class="w-6 h-6 rounded-sm border"
                        :class="currentColor.toLowerCase() === preset.value ? 'border-accent-primary border-2' : 'border-gray-400'"
                        :style="{ backgroundColor: preset.value }"
                        :title="preset.label"
                        @click.prevent="setColor(preset.value)"
                      ></button>
                    </div>
                    <label class="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 text-xs cursor-pointer">
                      <span>Custom</span>
                      <input
                        type="color"
                        class="h-6 w-6"
                        :value="currentColor"
                        @input="editor.chain().focus().setColor($event.target.value).run()"
                      />
                    </label>
                    <button
                      class="w-full mt-1 px-1 py-0.5 text-xs text-left hover:bg-gray-200 rounded-sm"
                      @click.prevent="unsetColor()"
                    >
                      Remove color
                    </button>
                  </div>
                </div>
                </div>
              <div class="flex flex-wrap border-r border-gray-300 pr-2">
                <button class="px-1 mx-0.5 py-0.5 w-fit hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap"
                @click.prevent="editor.chain().focus().setFontFamily('Aptos, sans-serif').run()"
                :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'Aptos, sans-serif' }) }"
                    >
                    Aptos
                </button>
                <button class="px-1 mx-0.5 py-0.5 w-fit hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap"
                @click.prevent="editor.chain().focus().setFontFamily('Calibri, sans-serif').run()"
                :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'Calibri, sans-serif' }) }"
                >
                Calibri
              </button>
                
                <button class="px-1 mx-0.5 py-0.5 w-fit hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().unsetFontFamily().run()">Unset font family</button>
              </div>
              <div class="flex flex-wrap border-r border-gray-300 pr-2">
                <select class="border p-1 rounded-md" @input="editor.chain().focus().setFontSize($event.target.value).run()" name="" id="" value="14.667px">
                  <option value="10.67px">8</option>
                  <option value="14.667px">11</option>
                  <option value="16px">12</option>
                  <option value="18.667px">14</option>
                  <option value="21.333px">16</option>
                  <option value="24px">18</option>
                  <option value="26.667px">20</option>
                  <option value="29.333px">22</option>
                  <option value="32px">24</option>
                </select>  
              </div>
          <div class="flex flex-wrap border-r border-gray-300 px-2">
                <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleBold().run()" :disabled="!editor.can().chain().focus().toggleBold().run()" :style="getToolbarWidgetStyle('bold')">
                    <strong>B</strong>
                </button>
                <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleItalic().run()" :disabled="!editor.can().chain().focus().toggleItalic().run()" :style="getToolbarWidgetStyle('italic')">
                    <em>I</em>
                </button>
                <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleStrike().run()" :disabled="!editor.can().chain().focus().toggleStrike().run()" :style="getToolbarWidgetStyle('strike')">
                    <s>S</s>
                </button>
              </div>
              <div  class="flex flex-wrap border-r border-gray-300 px-2">
                <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="setLink" :style="getToolbarWidgetStyle('link')">
                  <LinkSVGSmall fill="#171717"/>
                </button>
              </div>
              <div  class="flex flex-wrap border-r border-gray-300 px-2">
                <button class="px-1 mx-0.5 py-0.5  w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleBulletList().run()" :style="getToolbarWidgetStyle('bulletList')">
                  <BulletListSmallSVG fill="#171717"/>
                </button>
                <button class="px-1 mx-0.5 py-0.5  w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleOrderedList().run()" :style="getToolbarWidgetStyle('orderedList')">
                  <OrderedListSVGSmall fill="#171717"/>
                </button>
              </div>
                <!-- <button class="px-1 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleCode().run()" :disabled="!editor.can().chain().focus().toggleCode().run()" :class="{ 'is-active': editor.isActive('code') }">
                Code
                </button> -->

                <!-- <button class="px-1 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().setParagraph().run()" :class="{ 'is-active': editor.isActive('paragraph') }">
                Paragraph
                </button> -->
                <div class="flex flex-wrap border-r border-gray-300 px-2">
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleHeading({ level: 1 }).run()" :style="getToolbarHeaderStyle(1)">
                  H1
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleHeading({ level: 2 }).run()" :style="getToolbarHeaderStyle(2)">
                  H2
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleHeading({ level: 3 }).run()" :style="getToolbarHeaderStyle(3)">
                  H3
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleHeading({ level: 4 }).run()" :style="getToolbarHeaderStyle(4)">
                  H4
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleHeading({ level: 5 }).run()" :style="getToolbarHeaderStyle(5)">
                  H5
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleHeading({ level: 6 }).run()" :style="getToolbarHeaderStyle(6)">
                  H6
                  </button>
                </div>
                <div class="flex flex-wrap border-r border-gray-300 px-2">
                  <!-- <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleCodeBlock().run()" :class="{ 'is-active': editor.isActive('codeBlock') }">
                  Code block
                  </button> -->
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().toggleBlockquote().run()" :style="getToolbarWidgetStyle('blockquote')">
                    <BlockquoteSVGSmall
                      fill="#171717"
                    />
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().setHorizontalRule().run()">
                    <HorizontalRuleSVGSmall
                      fill="#171717"
                    />
                  </button>
                  <!-- <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().setHardBreak().run()">
                  Hard break
                  </button> -->

                  <!-- <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().clearNodes().run()">
                  Clear nodes
                  </button> -->
                </div>
                <div class="flex flex-wrap border-r border-gray-300 px-2">
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap" @click.prevent="editor.chain().focus().unsetAllMarks().clearNodes().run()">
                    <RemoveStylingSVGSmall
                      fill="#171717"
                    />
                  </button>
                </div>
                <div class="flex flex-wrap border-r border-gray-300 px-2">
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap disabled:text-neutral-4 disabled:hover:bg-neutral-1" @click.prevent="editor.chain().focus().undo().run()" :disabled="!editor.can().chain().focus().undo().run()">
                    <UndoSVGSmall
                      :fill="undoFill"
                    />
                  </button>
                  <button class="px-1 mx-0.5 py-0.5 w-6 h-6 hover:bg-gray-300 active:bg-gray-300 rounded-sm whitespace-nowrap disabled:text-neutral-4 disabled:hover:bg-neutral-1" @click.prevent="editor.chain().focus().redo().run()" :disabled="!editor.can().chain().focus().redo().run()">
                    <RedoSVGSmall
                      :fill="redoFill"
                    />
                  </button>
                </div>
            </div>
        </div>
        <editor-content class="bg-white px-1 w-full rounded-b-md outline-none active:outline-none focus:outline-none" :editor="editor" />
    </div>
</template>

<script setup>
import { defineProps, defineEmits, defineExpose, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import BulletListSmallSVG from '@/components/svg-components/BulletListSmallSVG.vue'
import RedoSVGSmall from '@/components/svg-components/RedoSVGSmall.vue'
import UndoSVGSmall from '@/components/svg-components/UndoSVGSmall.vue'
import HorizontalRuleSVGSmall from '@/components/svg-components/HorizontalRuleSVGSmall.vue'
import BlockquoteSVGSmall from '@/components/svg-components/BlockquoteSVGSmall.vue'
import RemoveStylingSVGSmall from '@/components/svg-components/RemoveStylingSVGSmall.vue'
import OrderedListSVGSmall from '@/components/svg-components/OrderedListSVGSmall.vue'
import LinkSVGSmall from '@/components/svg-components/LinkSVGSmall.vue'
import StarterKit from '@tiptap/starter-kit'
import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import { TextStyle, FontFamily, FontSize } from '@tiptap/extension-text-style'
import Image from '@tiptap/extension-image'
// import Link from '@tiptap/extension-link'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

// Preserve <img> tags (incl. cid: inline images like the email signature/logo)
// through an edit. StarterKit has no image node, so without this the editor
// silently drops them and the emailed signature arrives as an orphan attachment.
// Keep width/height/style so the signature keeps its intended size.
const InlineImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: { default: null },
            height: { default: null },
            style: { default: null },
        }
    },
})

// A transient, view-only highlight for text the app just changed (e.g. contact
// fields swapped when the draft's recipient is retargeted). Implemented as a
// ProseMirror decoration so it overlays the rendered view WITHOUT entering the
// document -- it never appears in getHTML(), never autosaves, and never goes out
// in the email. Cleared after the fade via a meta transaction.
const flashKey = new PluginKey('flashHighlight')
const FlashHighlight = Extension.create({
    name: 'flashHighlight',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: flashKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply(tr, old) {
                        const meta = tr.getMeta(flashKey)
                        if (meta && meta.decorations) return meta.decorations
                        if (meta && meta.clear) return DecorationSet.empty
                        // keep decorations aligned if the doc changes under them
                        return old.map(tr.mapping, tr.doc)
                    },
                },
                props: {
                    decorations(state) { return flashKey.getState(state) },
                },
            }),
        ]
    },
})

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['update:modelValue'])

const editor = ref(null)

const showColorPicker = ref(false)

const colorPresets = [
    { value: '#000000', label: 'Black' },
    { value: '#ff0000', label: 'Red' },
    { value: '#00ff00', label: 'Green' },
    { value: '#0000ff', label: 'Blue' },
    { value: '#cccccc', label: 'Grey' },
]

// Color of the current selection, falling back to black so the swatch preview
// and the custom input always have a valid hex value.
const currentColor = computed(() => {
    return editor.value?.getAttributes('textStyle')?.color || '#000000'
})

function setColor(color) {
    editor.value.chain().focus().setColor(color).run()
    showColorPicker.value = false
}

function unsetColor() {
    editor.value.chain().focus().unsetColor().run()
    showColorPicker.value = false
}

function closeColorPicker() {
    showColorPicker.value = false
}

const undoFill = computed(() => {
    return !editor.value.can().chain().focus().undo().run() ? '#A3A3A3' : '#171717'  
})

const redoFill = computed(() => {
    return  !editor.value.can().chain().focus().redo().run() ? '#A3A3A3' : '#171717'
})

watch(() => props.modelValue, (value) => {
    // const isSame = editor.value.getHTML() === props.value
    // if (isSame) {
    //     return
    // }
    // editor.value.commands.setContent(value, false)
    if (!editor.value.isFocused) {
        editor.value?.commands.setContent(value, false, { preserveWhitespace: 'full' });
    }
})

function getToolbarWidgetStyle(widgetName) {
    if(editor.value.isActive(widgetName)) {
        return 'background-color: #E5E5E5;'
    } else {
        return ''
    }
}

function getToolbarHeaderStyle(headerNumber) {
    if(editor.value.isActive('heading', { level: headerNumber })) {
        return 'background-color: #E5E5E5;'
    } else {
        return ''
    }
}

function setLink() {
    const previousUrl = editor.value.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    // cancelled
    if (url === null) {
        return
    }

    // empty
    if (url === '') {
        editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run()

        return
    }

    // update link
    editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run()
}

const fontValue = ref(null)

const fontChoices = ref([

])

onMounted(() => {
    editor.value = new Editor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            StarterKit.configure({paragraph: {
              HTMLAttributes: {
                style: 'margin: 0;'
              }
            }}),
            FontFamily,
            FontSize,
            InlineImage.configure({ inline: true, allowBase64: true }),
            FlashHighlight,
            // Link.configure({
            //     openOnClick: true,
            //     defaultProtocol: 'https',
            // }),
        ],
        editorProps: {
            attributes: {
                class: 'min-h-[400px] h-full overflow-y-auto p-2'
            }
        },
        content: props.modelValue,
        onUpdate: () => {
          emit('update:modelValue', editor.value.getHTML())
        },
    })
})

// Briefly highlight every occurrence of the given strings: amber holds for 3s
// then fades over 4s. Called by the parent after it swaps values into the body so
// the analyst sees what changed. Positions are resolved against the current doc; a
// value split across formatting boundaries just won't match (best-effort,
// view-only). The decoration carries no document change, so it triggers neither
// onUpdate nor an autosave.
function flashValues(values) {
    const ed = editor.value
    if (!ed || !values || !values.length) return
    const targets = [...new Set(values.filter(v => v && v.length >= 2))]
    if (!targets.length) return
    const decorations = []
    ed.state.doc.descendants((node, pos) => {
        if (!node.isText || !node.text) return
        const text = node.text
        for (const v of targets) {
            let from = 0
            for (;;) {
                const idx = text.indexOf(v, from)
                if (idx === -1) break
                decorations.push(Decoration.inline(pos + idx, pos + idx + v.length, { class: 'flash-highlight' }))
                from = idx + v.length
            }
        }
    })
    if (!decorations.length) return
    const set = DecorationSet.create(ed.state.doc, decorations)
    ed.view.dispatch(ed.state.tr.setMeta(flashKey, { decorations: set }))
    setTimeout(() => {
        // editor may have been torn down while the flash was pending. Matches the
        // 7s CSS animation (3s hold + 4s fade) so the decoration is removed only
        // once it's fully transparent.
        if (editor.value) {
            editor.value.view.dispatch(editor.value.state.tr.setMeta(flashKey, { clear: true }))
        }
    }, 7000)
}

// Insert an image at the cursor. Used by the mailbox signature editor, which
// needs to place a specific inline image (signature/logo) that the user has no
// other way to add -- there is no image button in the toolbar.
function insertImage(src) {
    if (!src || !editor.value) return
    editor.value.chain().focus().setImage({ src }).run()
}

defineExpose({ flashValues, insertImage })

onBeforeUnmount(() => {
    editor.value.destroy()
})

</script>

<style>
/* Basic editor styles */
    *:focus {
        outline: none;
    }

    /* Transient flash for app-updated text (see the FlashHighlight decoration).
       View-only overlay: holds amber for 3s, then fades to transparent over 4s
       (7s total), after which the decoration is removed. Never part of the
       document, so never emailed. */
    .flash-highlight {
        border-radius: 2px;
        animation: flashHighlightFade 7s ease-out forwards;
    }
    @keyframes flashHighlightFade {
        /* hold at full amber through 3s of the 7s (3 / 7 = 42.857%) */
        0%, 42.857% { background-color: rgba(250, 204, 21, 0.85); }
        100%        { background-color: rgba(250, 204, 21, 0); }
    }
    .tiptap {
    :first-child {
        margin-top: 0;
  }

    /* Link styles */
    a {
      color: #154B51;
      text-decoration: underline;
      cursor: pointer;

    &:hover {
      color: #9EDEE5;
      text-decoration: underline;
    }
  }

  /* List styles */
  ul {
    list-style: disc !important;
  }

  ol {
    list-style: decimal !important;
  }
  ul,
  ol {
    padding: 0 1rem;
    margin: 1rem 1rem 1rem 0.4rem;
    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    text-wrap: pretty;
  }

  h1,
  h2 {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: 1.4rem;
  }
  h2 {
    font-size: 1.2rem;
  }

  h3 {
    font-size: 1.1rem;
  }

  h4,
  h5,
  h6 {
    font-size: 1rem;
  }

  /* Code and preformatted text styles */
  code {
    background-color: #E5E5E5;
    border-radius: 0.4rem;
    color: #0A0A0A;
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  pre {
    background: #404040;
    border-radius: 0.5rem;
    color: #E5E5E5;
    font-family: 'JetBrainsMono', monospace;
    margin: 1rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid #D4D4D4;
    margin: 0.5rem 0;
    padding-left: 1rem;
  }

  hr {
    border: none;
    border-top: 1px solid #E5E5E5;
    margin: 1rem 0;
  }
  input:focus, textarea:focus, select:focus{
        outline: none;
    }

  @font-face {
    font-family: 'Calibri';
    font-style: normal;
    font-weight: 400;
    src: url(https://fonts.gstatic.com/l/font?kit=J7afnpV-BGlaFfdAhLEY67FIEjg&skey=a1029226f80653a8&v=v15) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  /* latin */
  @font-face {
    font-family: 'Calibri';
    font-style: normal;
    font-weight: 700;
    src: url(https://fonts.gstatic.com/l/font?kit=J7aanpV-BGlaFfdAjAo9_pxqHxIZrCE&skey=cd2dd6afe6bf0eb2&v=v15) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  
}
</style>